import { Tab } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { AgentData } from "context/Agent/DataProvider";
import { useEffect, useState, useContext } from "react";
import { useClaimsMapper } from "hooks/useClaimsMapper";
import { stringFormatingToPascal } from "js/formatting";
import { claimsFiltering } from "js/serviceAndClaimsFiltering";
import { getPolicyDetails } from "service/policy/getPolicyDetails";
import { AgentServiceClaimNavigationCardData } from "js/placeholderData/agents/agentServiceClaimNavigationCardData";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import SimpleCallToActionCard from "components/General/SimpleCallToActionCard";
import AppNavigationCard from "components/General/AppNavigationCard";
import ClaimsTab from "components/ServicesAndClaims/ClaimsTab";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import ServicesAndClaimsFilterModel from "components/ModalDialogs/ServicesAndClaimsFilterModel";
import config from "config/config";
import PolicyLookUpFromComboBoxModal from "components/AgentPortal/ModalDialogs/PolicyLookUpFromComboBoxModal";

export default function ServicesClaims() {
  let debounceFunction; //timer identifier
  let debounceTimer = 5; //timer in ms
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { agentPolicies, agentClaims } = useContext(AgentData);
  const [category, setCategory] = useState([]);
  const [tabGroupIndex, setTabGroupIndex] = useState(0);
  const [illustrationsDialogState, setIllustrationsDialogState] =
    useState(false);
  const { claims } = useClaimsMapper();
  const [claimsData, setClaimsData] = useState([]);
  const [modalState, setModalState] = useState(false);
  const [selectedClaimsCategory, setSelectedClaimsCategory] = useState("");
  const [startsWith, setStartsWith] = useState();
  const [type, setType] = useState();
  const [sortby, setSortby] = useState();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [policiesHasDisability, setPoliciesHasDisability] = useState(
    agentPolicies?.filter((x) => x.IsDisabilityClaimExist)
  );
  const [selectedPolicyId, setSelectedPolicyId] = useState();
  const [disabilityClaims, setDisabilityClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [illustrationModalState, setIllustrationModalState] = useState(false);

  async function onchange(policyId) {
    if (policyId) {
      let disabilityClaimsData = [];
      setLoading(true);
      await getPolicyDetails(policyId)
        .then((data) => {
          setLoading(false);
          if (data?.disabilityClaim?.length > 0) {
            data?.disabilityClaim.map((dClaim) => {
              disabilityClaimsData.push({
                claimStatus: dClaim.claimStatus,
                claimType: dClaim.claimType,
                title: `ID: ${dClaim.claimNumber}`,
                oldPolicy: data.oldPolicy,
                requestId: dClaim.claimNumber,
              });
            });
            setDisabilityClaims(disabilityClaimsData);
          }
        })
        .catch((error) => {
          setLoading(false);
          setDisabilityClaims([]);
          console.error("Something wrong.", error);
        });
    } else setDisabilityClaims([]);
  }
  useEffect(() => {
    if (tabGroupIndex === 0) {
      setType(searchParams.get("type"));
      setClaimsData(
        claimsFiltering(
          claims,
          selectedClaimsCategory,
          searchParams.get("type"),
          searchParams.get("sortby"),
          startsWith
        )
      );
    }
  }, [searchParams, selectedClaimsCategory]);
  useEffect(() => {
    setClaimsData(claims);
  }, [claims]);
  useEffect(() => {
    let claimsStatus = [];
    if (agentClaims?.length > 0) {
      agentClaims?.map((c) => {
        let claimType = claimsStatus?.find(
          (x) =>
            x?.value?.toLowerCase() ===
            c?.claimType?.toLowerCase().replace(" ", "")
        );
        if (!claimType && c?.claimType !== "")
          claimsStatus.push({
            value: c?.claimType?.replace(" ", "")?.toLowerCase(),
            display: stringFormatingToPascal(c?.claimType),
          });
      });
      setCategory(claimsStatus);
    }
  }, [agentClaims]);

  useBreadcrumb([
    { name: t("AgentClaims-home"), url: "/dashboard" },
    { name: t("AgentServiceAndClaimsTab-claims"), url: "#" },
  ]);

  return (
    <>
      <div className="container mb-8">
        <h1>{t("AgentClaims-claimsTitle")}</h1>
        <p>{t("AgentClaims-claimssub-title")}</p>
        <hr />
      </div>
      <div className="container mb-8">
        <h2>{t("AgentClaims-claimsContainerTitle1")}</h2>
        <hr />
      </div>
      <div className="container ui-component-spacing">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {config.CALCFOCUS_SERVICE_BASE_URL ? (
            <SimpleCallToActionCard
              title={t("AgentClaims-actionCardTitle1")}
              desc={t("AgentClaims-actioncardsub-title1")}
              ctaText={t("AgentClaims-actioncard-button1")}
              isUrlNeedToOpenInNewTab={true}
              performSubmit={() => setIllustrationModalState(true)}
            />
          ) : config.LIFE_SALES_CENTRAL_URL ? (
            <SimpleCallToActionCard
              title={t("AgentClaims-actionCardTitle1")}
              desc={t("AgentClaims-actioncardsub-title1")}
              ctaText={t("AgentClaims-actioncard-button1")}
              ctaUrl={config.LIFE_SALES_CENTRAL_URL}
              isUrlNeedToOpenInNewTab={true}
            />
          ) : (
            <SimpleCallToActionCard
              title="Run Customized Quotes & Illustrations"
              desc="Log into Life Sales Central to get quotes and illustrations"
              ctaText="Log into Life Sales Central to get quotes and illustrations"
              ctaUrl="disable"
            />
          )}

          <SimpleCallToActionCard
            title={t("AgentClaims-actionCardTitle2")}
            desc={t("AgentClaims-actioncardsub-title2")}
            ctaText={t("AgentClaims-actionCardbutton2")}
            ctaUrl="/services-claims/forms"
          />
          <SimpleCallToActionCard
            title={t("AgentClaims-actionCardTitle3")}
            desc={t("AgentClaims-actionCardSubTitle3")}
            ctaText={t("AgentClaims-actionCardbutton3")}
            ctaUrl="/payment"
          />
          <SimpleCallToActionCard
            title={t("AgentClaims-actionCardTitle4")}
            desc={t("AgentClaims-actionCardSubTitle4")}
            ctaText={t("AgentClaims-actionCardbutton4")}
            ctaUrl="/services-claims/claims/file-a-claim"
          />
        </div>
      </div>
      <div className="container ui-component-spacing">
        <h2>{t("AgentClaims-claimsContainerTitle2")}</h2>
        <hr />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr">
          {AgentServiceClaimNavigationCardData().map((item, idx) => (
            <AppNavigationCard
              key={`agent-app-navigation-card-${idx}`}
              icon={item.icon}
              title={item.title}
              desc={item.desc}
              url={item.url}
            />
          ))}
        </div>
      </div>
      <div className="container ui-component-spacing">
        <h2>{t("AgentClaims-claimsContainerTitle3")}</h2>
        <hr />

        <Tab.Group
          onChange={(tabGroupIndex) => {
            setTabGroupIndex(tabGroupIndex);
          }}
          selectedIndex={selectedTabIndex}
        >
          <div className="ui-card flex-grow px-2 p-1 md:px-4">
            <Tab.List>
              <Tab
                className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                  selectedTabIndex === 0 ? "" : "text-ui-blue"
                }`}
                onClick={() => {
                  setSelectedTabIndex(0);
                }}
              >
                {t("AgentClaims-claimsTab1")}
              </Tab>
              {config.ENABLE_DISABILITYCLAIMS === "true" && (
                <Tab
                  className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                    selectedTabIndex === 1 ? "" : "text-ui-blue"
                  }`}
                  onClick={() => {
                    setSelectedTabIndex(1);
                  }}
                >
                  {t("AgentClaims-claimsTab2")}
                </Tab>
              )}
            </Tab.List>
          </div>

          <Tab.Panels className="mt-4 lg:mt-6">
            <Tab.Panel>
              {selectedTabIndex === 0 && (
                <>
                  <div className="mt-4 lg:flex lg:items-start lg:justify-between lg:mt-6">
                    {tabGroupIndex === 0 && (
                      <div className="hidden lg:flex lg:items-center">
                        <label
                          id="ID_categoryLabel"
                          htmlFor="ID_category"
                          className="mr-2 whitespace-nowrap"
                        >
                          {t("AgentClaims-claimsPanelLabel1")}
                        </label>
                        <select
                          id="ID_category"
                          aria-labelledby="ID_categoryLabel"
                          value={selectedClaimsCategory}
                          onChange={(e) => {
                            setClaimsData(
                              claimsFiltering(
                                claims,
                                e.target.value?.trim(),
                                type,
                                sortby,
                                startsWith
                              )
                            );
                            setSelectedClaimsCategory(e.target.value?.trim());
                          }}
                        >
                          <option value="">
                            {t("AgentClaims-claimsPanelOption1")}
                          </option>
                          <option value="dth">
                            {t("AgentClaims-claimsPanelOption2")}
                          </option>
                        </select>
                      </div>
                    )}
                    <div className="hidden lg:flex lg:items-start lg:mx-4">
                      <label
                        id="ID_searchPolicyLabel"
                        htmlFor="ID_searchPolicy"
                        className="mt-2 mr-2"
                      >
                        {t("AgentClaims-claimspanel-label2")}
                      </label>
                      <div>
                        {tabGroupIndex === 0 && (
                          <div className="flex flex-col flex-1 w-full relative">
                            <input
                              id="ID_searchPolicy"
                              aria-labelledby="ID_searchPolicyLabel"
                              aria-describedby="ID_searchPolicyError"
                              type="text"
                              className="pl-8 w-[350px] "
                              defaultValue={startsWith}
                              placeholder="Please enter contract number"
                              onChange={(e) => setStartsWith(e.target.value)}
                              onKeyUp={() => {
                                clearTimeout(debounceFunction);
                                debounceFunction = setTimeout(async () => {
                                  setClaimsData(
                                    claimsFiltering(
                                      claims,
                                      selectedClaimsCategory,
                                      type,
                                      sortby,
                                      startsWith
                                    )
                                  );
                                }, debounceTimer);
                              }}
                              onKeyDown={() => {
                                clearTimeout(debounceFunction);
                              }}
                            />
                            <svg
                              className="ui-icon text-theme-action w-4 h-4 absolute ml-2 mt-3"
                              focusable="false"
                              aria-hidden="true"
                              role="presentation"
                            >
                              <use href="#ui-icon-search"></use>
                            </svg>
                          </div>
                        )}
                        <div
                          id="ID_searchPolicyError"
                          className="text-ui-red"
                        ></div>
                      </div>
                    </div>

                    <div>
                      <button
                        className="ui-btn-primary"
                        onClick={() => setModalState(true)}
                      >
                        {t("AgentClaims-filterButton")}
                      </button>
                    </div>
                  </div>
                  {claimsData?.length === 0 ? (
                    <div className="border border-dashed border-ui-gray p-6 my-6">
                      <div className="text-center">
                        <div className="text-xl font-bold">
                          {t("AgentClaims-defaultMessageTitle1")}
                        </div>
                        <div className="mt-1 text-theme-text-quaternary text-sm">
                          {t("AgentClaims-defaultMessageSubTitle1")}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <ClaimsTab data={claimsData} />
                  )}
                </>
              )}
            </Tab.Panel>
            {config.ENABLE_DISABILITYCLAIMS === "true" && (
              <Tab.Panel>
                {selectedTabIndex === 1 && (
                  <EverlakeLoadingOverlay>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                      <div className="my-4 md:my-0 md:flex-strech md:items-center pt-3">
                        {policiesHasDisability?.length > 0 && (
                          <div className="mb-8 md:mb-8">
                            <label
                              id="ID_messageDateLabel"
                              htmlFor="ID_messageDate"
                            >
                              {t("AgentClaims-claimsPanelLabel3")}
                            </label>
                            <select
                              id="ID_messageDate"
                              aria-labelledby="ID_messageDateLabel"
                              className="bg-white"
                              value={selectedPolicyId}
                              onChange={(e) => {
                                setSelectedPolicyId(e.target.value);
                                onchange(e.target.value);
                              }}
                            >
                              <option value="">
                                - {t("AgentClaims-claimsPanelOption3")} -
                              </option>
                              {policiesHasDisability.map((policy, idx) => {
                                return (
                                  <option
                                    value={policy?.contractID}
                                    key={`main category - ${idx}`}
                                  >
                                    {policy?.oldPolicy}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                    {(selectedPolicyId &&
                      disabilityClaims?.length === 0 &&
                      !loading) ||
                    policiesHasDisability?.length === 0 ? (
                      <div className="border border-dashed border-ui-gray p-6 my-6">
                        <div className="text-center">
                          <div className="text-xl font-bold">
                            {t("AgentClaims-defaultMessageTitle2")}
                          </div>
                          <div className="mt-1 text-theme-text-quaternary text-sm">
                            {t("AgentClaims-defaultMessageSubTitle2")}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <ClaimsTab data={disabilityClaims} />
                    )}
                  </EverlakeLoadingOverlay>
                )}
              </Tab.Panel>
            )}
          </Tab.Panels>
        </Tab.Group>
      </div>
      <ServicesAndClaimsFilterModel
        modalState={modalState}
        setModalState={setModalState}
      />
      <GeneralPurposeDialog
        state={illustrationsDialogState}
        setState={setIllustrationsDialogState}
        title={t("AgentClaims-dialogTitle")}
        desc={t("AgentClaims-dialogSubTitle")}
      />
      <PolicyLookUpFromComboBoxModal
        isOpenPolicyLookup={illustrationModalState}
        setIsOpenPolicyLookup={setIllustrationModalState}
      />
    </>
  );
}
